import React from 'react';
import { usePrdStore } from '../store/prd-store';
import { PrdSection, PrdRequirement, UserStory } from '../types';

export const EditorSidebar: React.FC = () => {
  const {
    document,
    selectedSectionId,
    selectedRequirementId,
    selectedUserStoryId,
    expandedSections,
    selectSection,
    selectRequirement,
    selectUserStory,
    toggleSectionExpanded,
    addSection,
  } = usePrdStore();

  if (!document) return null;

  const handleAddSection = () => {
    const title = prompt('Enter section title:');
    if (title) {
      const section: PrdSection = {
        id: `section-${Date.now()}`,
        type: 'custom',
        title,
        content: '',
        order: document.sections.length,
      };
      addSection(section);
    }
  };

  return (
    <div className="prd-sidebar w-64 border-r bg-gray-50 dark:bg-gray-900 overflow-y-auto">
      <div className="p-4">
        <h2 className="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider mb-3">
          Outline
        </h2>

        {/* Sections */}
        <div className="space-y-1">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Sections</h3>
            <button
              onClick={handleAddSection}
              className="text-blue-500 hover:text-blue-600 text-sm"
              title="Add section"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </button>
          </div>
          
          {document.sections
            .sort((a, b) => a.order - b.order)
            .map(section => (
              <button
                key={section.id}
                onClick={() => selectSection(section.id)}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                  selectedSectionId === section.id
                    ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300'
                    : 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300'
                }`}
              >
                {section.title}
              </button>
            ))}
        </div>

        {/* Requirements */}
        {document.requirements.length > 0 && (
          <div className="mt-6 space-y-1">
            <button
              onClick={() => toggleSectionExpanded('requirements')}
              className="flex items-center justify-between w-full text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              <span>Requirements ({document.requirements.length})</span>
              <svg
                className={`w-4 h-4 transition-transform ${
                  expandedSections.includes('requirements') ? 'rotate-90' : ''
                }`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
            
            {expandedSections.includes('requirements') && (
              <div className="space-y-1 pl-3">
                {document.requirements.map(req => (
                  <button
                    key={req.id}
                    onClick={() => selectRequirement(req.id)}
                    className={`w-full text-left px-3 py-1.5 rounded-lg text-sm transition-colors ${
                      selectedRequirementId === req.id
                        ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300'
                        : 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-400'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="truncate">{req.title}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                        req.priority === 'must-have' ? 'bg-red-100 text-red-600' :
                        req.priority === 'should-have' ? 'bg-orange-100 text-orange-600' :
                        req.priority === 'could-have' ? 'bg-yellow-100 text-yellow-600' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {req.priority.split('-')[0]}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* User Stories */}
        {document.userStories.length > 0 && (
          <div className="mt-6 space-y-1">
            <button
              onClick={() => toggleSectionExpanded('user-stories')}
              className="flex items-center justify-between w-full text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              <span>User Stories ({document.userStories.length})</span>
              <svg
                className={`w-4 h-4 transition-transform ${
                  expandedSections.includes('user-stories') ? 'rotate-90' : ''
                }`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
            
            {expandedSections.includes('user-stories') && (
              <div className="space-y-1 pl-3">
                {document.userStories.map(story => (
                  <button
                    key={story.id}
                    onClick={() => selectUserStory(story.id)}
                    className={`w-full text-left px-3 py-1.5 rounded-lg text-sm transition-colors ${
                      selectedUserStoryId === story.id
                        ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300'
                        : 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-400'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="truncate">{story.title}</span>
                      {story.effort && (
                        <span className="text-xs text-gray-500">{story.effort}pt</span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Document Stats */}
        <div className="mt-8 p-3 bg-gray-100 dark:bg-gray-800 rounded-lg">
          <h3 className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase mb-2">
            Statistics
          </h3>
          <div className="space-y-1 text-xs text-gray-600 dark:text-gray-400">
            <div className="flex justify-between">
              <span>Sections</span>
              <span>{document.sections.length}</span>
            </div>
            <div className="flex justify-between">
              <span>Requirements</span>
              <span>{document.requirements.length}</span>
            </div>
            <div className="flex justify-between">
              <span>User Stories</span>
              <span>{document.userStories.length}</span>
            </div>
            <div className="flex justify-between">
              <span>Last Updated</span>
              <span>{new Date(document.updatedAt).toLocaleDateString()}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};